import React from "react"
import { AbsoluteFill, Sequence, interpolate, spring, useCurrentFrame, useVideoConfig } from "remotion"
import { msToFrames } from "../../utils/timing"
import type { Track } from "../../schemas"
import type { ZoomParams, CameraShakeParams, ColorGradeParams, OverlayShapeParams, FlashParams, VignetteParams } from "../../schemas"

interface EffectsLayerProps {
  effectsTrack: Track | undefined
  children: React.ReactNode
}

export const EffectsLayer: React.FC<EffectsLayerProps> = ({ effectsTrack, children }) => {
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()

  if (!effectsTrack) {
    return <AbsoluteFill>{children}</AbsoluteFill>
  }

  const segments = effectsTrack.sections.flatMap(s => s.segments)
  const currentMs = (frame / fps) * 1000

  // Collect active transform/filter effects at current frame
  let totalScale = 1
  let totalOriginX = 0.5
  let totalOriginY = 0.5
  let shakeX = 0
  let shakeY = 0
  let brightness = 1
  let contrast = 1
  let saturate = 1
  let hueRotate = 0

  const overlaySegments: Array<{ segment: Record<string, any>; params: OverlayShapeParams }> = []
  const flashSegments: Array<{ segment: Record<string, any>; params: FlashParams }> = []
  const vignetteSegments: Array<{ segment: Record<string, any>; params: VignetteParams }> = []

  for (const segment of segments) {
    if (!segment.enabled) continue
    const inMs = segment.timeline?.in ?? 0
    const outMs = segment.timeline?.out ?? 0
    if (currentMs < inMs || currentMs > outMs) continue

    const effectType = segment.effectType as string
    const params = segment.params as Record<string, any>

    if (effectType === "zoom") {
      const p = params as ZoomParams
      const inFrame = msToFrames(inMs, fps)
      const outFrame = msToFrames(outMs, fps)
      const durationInFrames = Math.max(outFrame - inFrame, 1)
      const localFrame = frame - inFrame
      totalScale *= interpolate(localFrame, [0, durationInFrames], [p.startScale ?? 1, p.endScale ?? 1.2], {
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
      })
      totalOriginX = p.originX ?? 0.5
      totalOriginY = p.originY ?? 0.5
    }

    if (effectType === "camera_shake") {
      const p = params as CameraShakeParams
      const intensity = p.intensity ?? 4
      const frequency = p.frequency ?? 0.5
      let decayMultiplier = 1
      if (p.decay) {
        const inFrame = msToFrames(inMs, fps)
        const outFrame = msToFrames(outMs, fps)
        const progress = (frame - inFrame) / Math.max(outFrame - inFrame, 1)
        decayMultiplier = Math.max(0, 1 - progress)
      }
      shakeX += Math.sin(frame * frequency * 0.7) * intensity * decayMultiplier
      shakeY += Math.cos(frame * frequency * 0.9) * intensity * decayMultiplier
    }

    if (effectType === "color_grade") {
      const p = params as ColorGradeParams
      brightness *= p.brightness ?? 1
      contrast *= p.contrast ?? 1
      saturate *= p.saturate ?? 1
      hueRotate += p.temperature ?? 0
    }

    if (effectType === "overlay_shape") {
      overlaySegments.push({ segment, params: params as OverlayShapeParams })
    }

    if (effectType === "flash") {
      flashSegments.push({ segment, params: params as FlashParams })
    }

    if (effectType === "vignette") {
      vignetteSegments.push({ segment, params: params as VignetteParams })
    }
  }

  const hasTransform = totalScale !== 1 || shakeX !== 0 || shakeY !== 0
  const hasFilter = brightness !== 1 || contrast !== 1 || saturate !== 1 || hueRotate !== 0

  const transformStyle = hasTransform
    ? `scale(${totalScale}) translate(${shakeX}px, ${shakeY}px)`
    : undefined
  const filterStyle = hasFilter
    ? `brightness(${brightness}) contrast(${contrast}) saturate(${saturate}) hue-rotate(${hueRotate}deg)`
    : undefined

  return (
    <AbsoluteFill>
      {/* Video with transform/filter effects applied */}
      <AbsoluteFill
        style={{
          overflow: "hidden",
          transform: transformStyle,
          transformOrigin: `${totalOriginX * 100}% ${totalOriginY * 100}%`,
          filter: filterStyle,
        }}
      >
        {children}
      </AbsoluteFill>

      {/* Overlay shapes rendered on top (legacy — kept for existing clips) */}
      {overlaySegments.map(({ segment, params }) => {
        const inFrame = msToFrames(segment.timeline?.in ?? 0, fps)
        const outFrame = msToFrames(segment.timeline?.out ?? 0, fps)
        const duration = Math.max(outFrame - inFrame, 1)

        return (
          <Sequence key={segment.id} from={inFrame} durationInFrames={duration}>
            <OverlayShape params={params} />
          </Sequence>
        )
      })}

      {/* Flash effects */}
      {flashSegments.map(({ segment, params }) => {
        const inFrame = msToFrames(segment.timeline?.in ?? 0, fps)
        const outFrame = msToFrames(segment.timeline?.out ?? 0, fps)
        const duration = Math.max(outFrame - inFrame, 1)

        return (
          <Sequence key={segment.id} from={inFrame} durationInFrames={duration}>
            <FlashEffect params={params} />
          </Sequence>
        )
      })}

      {/* Vignette effects */}
      {vignetteSegments.map(({ segment, params }) => {
        const inFrame = msToFrames(segment.timeline?.in ?? 0, fps)
        const outFrame = msToFrames(segment.timeline?.out ?? 0, fps)
        const duration = Math.max(outFrame - inFrame, 1)

        return (
          <Sequence key={segment.id} from={inFrame} durationInFrames={duration}>
            <VignetteEffect params={params} />
          </Sequence>
        )
      })}
    </AbsoluteFill>
  )
}

const OverlayShape: React.FC<{ params: OverlayShapeParams }> = ({ params }) => {
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()

  const { shape, color, xPct, yPct, size, strokeWidth, animation, rotation } = {
    shape: "circle" as const,
    color: "#FF0000",
    xPct: 0.5,
    yPct: 0.3,
    size: 80,
    strokeWidth: 4,
    animation: "pop_in" as const,
    rotation: 0,
    ...params,
  }

  let animScale = 1
  let animOpacity = 1

  if (animation === "pop_in") {
    const s = spring({ frame, fps, config: { damping: 12, stiffness: 200 } })
    animScale = s
    animOpacity = s
  } else if (animation === "bounce") {
    const s = spring({ frame, fps, config: { damping: 8, stiffness: 180 } })
    animScale = s
  } else if (animation === "pulse") {
    animScale = 1 + Math.sin(frame * 0.15) * 0.08
  }

  const baseStyle: React.CSSProperties = {
    position: "absolute",
    left: `${xPct * 100}%`,
    top: `${yPct * 100}%`,
    transform: `translate(-50%, -50%) scale(${animScale}) rotate(${rotation}deg)`,
    opacity: animOpacity,
  }

  if (shape === "circle") {
    return (
      <div
        style={{
          ...baseStyle,
          width: size,
          height: size,
          borderRadius: "50%",
          border: `${strokeWidth}px solid ${color}`,
          boxShadow: `0 0 ${size * 0.25}px ${color}40`,
        }}
      />
    )
  }

  if (shape === "arrow") {
    return (
      <div style={baseStyle}>
        <svg width={size} height={size} viewBox="0 0 100 100" fill="none">
          <path
            d="M20 50 L70 50 M55 30 L75 50 L55 70"
            stroke={color}
            strokeWidth={strokeWidth * 2}
            strokeLinecap="round"
            strokeLinejoin="round"
            filter={`drop-shadow(0 0 ${size * 0.08}px ${color}60)`}
          />
        </svg>
      </div>
    )
  }

  if (shape === "rectangle") {
    return (
      <div
        style={{
          ...baseStyle,
          width: size * 1.5,
          height: size,
          borderRadius: size * 0.08,
          border: `${strokeWidth}px solid ${color}`,
          boxShadow: `0 0 ${size * 0.2}px ${color}40`,
        }}
      />
    )
  }

  return null
}

// Flash effect — quick white/colored flash at the start of the segment
const FlashEffect: React.FC<{ params: FlashParams }> = ({ params }) => {
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()

  const color = params.color ?? "#FFFFFF"
  const intensity = params.intensity ?? 0.8
  const durationFrames = params.durationFrames ?? 3

  // Flash ramps up then fades out
  const opacity = durationFrames <= 2
    ? interpolate(frame, [0, 1, durationFrames], [0, intensity, 0], {
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
      })
    : interpolate(frame, [0, 1, durationFrames - 1, durationFrames], [0, intensity, intensity, 0], {
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
      })

  if (opacity <= 0) return null

  return (
    <AbsoluteFill
      style={{
        backgroundColor: color,
        opacity,
        pointerEvents: "none",
      }}
    />
  )
}

// Vignette effect — darkened edges with radial gradient
const VignetteEffect: React.FC<{ params: VignetteParams }> = ({ params }) => {
  const intensity = params.intensity ?? 0.5
  const size = params.size ?? 0.6

  return (
    <AbsoluteFill
      style={{
        background: `radial-gradient(ellipse at center, transparent ${size * 100}%, rgba(0,0,0,${intensity}) 100%)`,
        pointerEvents: "none",
      }}
    />
  )
}
